import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';
const TEAL_MID = '#4BBBC4';

const { PRIVATE_ROUTE } = API_ROUTES;

export default function EditPrebookingModal({ open, onClose, record, onSuccess }) {
  const { authAxios } = useContext(FetchContext);
  const [formData, setFormData] = useState({ title: '', author: '', quote_number: '', description: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && record) {
      setFormData({
        title: record.title || '',
        author: record.author || '',
        quote_number: record.quote_number || '',
        description: record.description || '',
      });
      setErrors({});
    }
  }, [open, record]);

  const handleChange = (field, value) => {
    setFormData((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.author.trim()) newErrors.author = 'Author is required';
    if (!formData.quote_number.trim()) newErrors.quote_number = 'Quote number is required';
    else if (!/^[a-zA-Z0-9\s\-_./#@(),:]+$/.test(formData.quote_number.trim()))
      newErrors.quote_number = 'Quote number contains invalid characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    try {
      await authAxios.post(`${PRIVATE_ROUTE.EDIT_PREBOOKING}${record._id}`, {
        title: formData.title.trim(),
        author: formData.author.trim(),
        quote_number: formData.quote_number.trim(),
        description: formData.description || null,
        date: record.date || null,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error('Failed to update prebooking', e);
    }
  };

  const fieldLabelSx = { fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 0.5 };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: 520 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BookOnlineIcon sx={{ color: '#fff', fontSize: 22 }} />
            <Box>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}
              >
                PREBOOKING COLLECTION
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                Edit Prebooking
              </Typography>
              {record?.date && (
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  {record.date.substring(0, 10)}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2.5 }}>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <Typography sx={{ ...fieldLabelSx, color: errors.title ? '#d32f2f' : '#555' }}>
                Title <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
              />
            </Grid>

            {/* Author */}
            <Grid item xs={12}>
              <Typography sx={{ ...fieldLabelSx, color: errors.author ? '#d32f2f' : '#555' }}>
                Author <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter author name"
                value={formData.author}
                onChange={(e) => handleChange('author', e.target.value)}
                error={!!errors.author}
                helperText={errors.author}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
              />
            </Grid>

            {/* Quote Number */}
            <Grid item xs={12}>
              <Typography sx={{ ...fieldLabelSx, color: errors.quote_number ? '#d32f2f' : '#555' }}>
                Quote Number <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter quote number"
                value={formData.quote_number}
                onChange={(e) => handleChange('quote_number', e.target.value)}
                error={!!errors.quote_number}
                helperText={errors.quote_number}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
              />
            </Grid>

            {/* Date (read-only) */}
            <Grid item xs={12}>
              <Typography sx={fieldLabelSx}>Date</Typography>
              <TextField
                fullWidth
                size="small"
                value={record?.date ? record.date.substring(0, 10) : ''}
                disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333', cursor: 'not-allowed' } }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography sx={fieldLabelSx}>
                Description{' '}
                <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${TEAL_LIGHT}`,
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: TEAL_MID,
                color: TEAL_DARK,
                '&:hover': { borderColor: TEAL_DARK, backgroundColor: TEAL_LIGHT },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                backgroundColor: TEAL_DARK,
                '&:hover': { backgroundColor: TEAL },
                fontWeight: 700,
                px: 3,
              }}
            >
              Update
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

EditPrebookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSuccess: PropTypes.func,
};
